import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Loader2 } from 'lucide-react';
import SatelliteMeasure from '../components/measurements/SatelliteMeasure';
import measurementApi from '../services/measurementApi';
import api, { serviceTypesApi } from '../services/api';

/**
 * SatelliteMeasurePage - Full-page wrapper for SatMeasure component
 * 
 * Route: /jobs/:jobId/measure
 * 
 * Loads the job, its customer, and any existing measurement.
 * Creates/updates measurement tied to the job.
 */
const SatelliteMeasurePage = () => {
  const { jobId } = useParams();
  const navigate = useNavigate();
  
  const [job, setJob] = useState(null);
  const [serviceTypes, setServiceTypes] = useState([]);
  const [existingMeasurement, setExistingMeasurement] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        setError(null);

        // Load job with customer info
        const jobRes = await api.get(`/jobs/${jobId}`);
        const jobData = jobRes.data;
        
        if (!jobData) {
          setError('Job not found');
          return;
        }
        
        setJob(jobData);

        // Load service types for pricing
        const serviceTypesRes = await serviceTypesApi.getAll();
        setServiceTypes(serviceTypesRes.data || []);

        // Check for existing measurement on this job
        try {
          const measurementRes = await measurementApi.getByJob(jobId);
          if (measurementRes) {
            setExistingMeasurement(measurementRes);
          }
        } catch (err) {
          // No existing measurement - that's fine
          console.log('No existing measurement for job');
        }

      } catch (err) {
        console.error('Failed to load data:', err);
        setError(err.response?.data?.message || 'Failed to load job data');
      } finally {
        setLoading(false);
      }
    };

    if (jobId) {
      loadData();
    }
  }, [jobId]);

  const handleSave = async (measurementData) => {
    setSaving(true);
    try {
      // Add jobId to the measurement data
      const dataWithJob = {
        ...measurementData,
        jobId: parseInt(jobId),
      };

      if (existingMeasurement?.id) {
        // Update existing measurement
        await measurementApi.update(existingMeasurement.id, dataWithJob);
      } else {
        // Create new measurement
        await measurementApi.create(dataWithJob);
      }
      
      // Navigate back to jobs page (or could go to job detail)
      navigate('/jobs', { 
        state: { 
          message: 'Measurement saved successfully',
          highlightJobId: jobId 
        } 
      });
    } catch (err) {
      console.error('Failed to save measurement:', err);
      throw new Error(err.response?.data?.message || 'Failed to save measurement');
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    navigate(-1); // Go back to previous page
  };

  // Loading state
  if (loading) {
    return (
      <div className="h-screen flex items-center justify-center bg-gray-100">
        <div className="text-center">
          <Loader2 className="w-10 h-10 text-primary-600 animate-spin mx-auto mb-3" />
          <p className="text-gray-600">Loading job data...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error || !job) {
    return (
      <div className="h-screen flex items-center justify-center bg-gray-100">
        <div className="text-center">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <span className="text-2xl">⚠️</span>
          </div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">
            {error || 'Job not found'}
          </h2>
          <button
            onClick={() => navigate('/jobs')}
            className="text-primary-600 hover:underline flex items-center gap-1 mx-auto"
          >
            <ArrowLeft size={16} />
            Back to Jobs
          </button>
        </div>
      </div>
    );
  }

  // Build customer object from job data
  // The job should have customer info nested or we use job's address
  const customer = job.customer || {
    id: job.customerId,
    name: job.customerName || 'Customer',
    address: job.address,
    city: job.city,
    state: job.state,
    zipCode: job.zipCode,
  };

  // Initial address from job (service location may differ from customer address)
  const initialAddress = [job.address, job.city, job.state, job.zipCode]
    .filter(Boolean)
    .join(', ');

  return (
    <div className="h-screen flex flex-col">
      <SatelliteMeasure
        customer={customer}
        initialAddress={initialAddress}
        serviceTypes={serviceTypes}
        existingMeasurement={existingMeasurement}
        onSave={handleSave}
        onCancel={handleCancel}
        readOnly={false}
      />
    </div>
  );
};

export default SatelliteMeasurePage;